// Renders a single Artifact returned by the backend: routes each kind to its
// renderer in artifact-renderers/ and guards them with a raw-text fallback.
// The heavier libraries (mermaid, cytoscape) are lazy-loaded inside their
// renderers so they don't bloat the initial bundle.

import { Component, useState, type ReactNode } from "react";

import type { Artifact } from "@/types/eryndal";
import { cn } from "@/utils/cn";
import {
  CsvArtifact,
  CytoscapeArtifact,
  HtmlArtifact,
  MarkdownArtifact,
  MermaidArtifact,
  RawArtifact,
  XlsxArtifact,
} from "./artifact-renderers";
import { EvidenceMatrix } from "./EvidenceMatrix";

interface Props {
  artifact: Artifact;
  className?: string;
}

/**
 * Catches a throwing artifact renderer and swaps in the raw payload rather than
 * taking down the drawer (and with it the rest of the shell).
 */
class ArtifactErrorBoundary extends Component<
  { artifact: Artifact; children: ReactNode },
  { failed: boolean; message: string | null }
> {
  state = { failed: false, message: null as string | null };

  static getDerivedStateFromError(error: unknown) {
    return {
      failed: true,
      message: error instanceof Error ? error.message : String(error),
    };
  }

  componentDidCatch(error: unknown) {
    console.error("[ArtifactBlock] renderer threw, falling back to raw text", error);
  }

  render() {
    if (this.state.failed) {
      return <RawArtifact artifact={this.props.artifact} reason={this.state.message} />;
    }
    return this.props.children;
  }
}

export function ArtifactBlock({ artifact, className }: Props) {
  // Manual escape hatch, alongside the automatic fallback above: if a renderer
  // produces something wrong rather than something that throws, the reader can
  // still get at the underlying content without asking for a re-run.
  const [forceRaw, setForceRaw] = useState(false);

  return (
    <div className={cn(className)}>
      {forceRaw ? (
        <RawArtifact artifact={artifact} />
      ) : (
        <ArtifactErrorBoundary artifact={artifact} key={artifact.payload}>
          {renderArtifactBody(artifact)}
        </ArtifactErrorBoundary>
      )}
      <button
        type="button"
        onClick={() => setForceRaw((v) => !v)}
        className="mt-2 font-mono text-[0.66rem] text-warmgray-400 transition-colors duration-200 ease-atelier hover:text-oxblood-500"
      >
        {forceRaw ? "← back to rendered view" : "show as text"}
      </button>
    </div>
  );
}

/** Renders the artifact body for a kind. The outer wrapper is ArtifactBlock's. */
function renderArtifactBody(artifact: Artifact) {
  switch (artifact.kind) {
    case "csv":
      return (
        <div>
          <CsvArtifact artifact={artifact} />
        </div>
      );
    case "table": {
      // The model may emit either a real <table>…</table> HTML payload or a
      // GFM-style "| Col | Col | / |---|---|" markdown table. Route to the
      // right renderer so the markdown variant doesn't end up as a wall of
      // pipes inside a <div>.
      const payload = artifact.payload ?? "";
      const looksLikeMarkdownTable = !/<table[\s>]/i.test(payload) && /\|.*\|/.test(payload);
      const rendered = looksLikeMarkdownTable ? (
        <MarkdownArtifact artifact={artifact} />
      ) : (
        <HtmlArtifact artifact={artifact} kindLabel="table" />
      );
      return <div>{rendered}</div>;
    }
    case "html":
      return (
        <div>
          <HtmlArtifact artifact={artifact} kindLabel="document" />
        </div>
      );
    case "markdown":
      return (
        <div>
          <MarkdownArtifact artifact={artifact} />
        </div>
      );
    case "xlsx": {
      // Evidence-matrix bundles (ROADMAP §5.1) carry compact rows in
      // metadata for a scannable inline table; everything else falls back
      // to the generic download-only xlsx frame.
      const isEvidenceMatrix =
        artifact.metadata?.artifact_subtype === "evidence_matrix";
      return (
        <div>
          {isEvidenceMatrix ? (
            <EvidenceMatrix artifact={artifact} />
          ) : (
            <XlsxArtifact artifact={artifact} />
          )}
        </div>
      );
    }
    case "mermaid":
      return (
        <div>
          <MermaidArtifact artifact={artifact} />
        </div>
      );
    case "cytoscape":
      return (
        <div>
          <CytoscapeArtifact artifact={artifact} />
        </div>
      );
    default:
      return null;
  }
}
